/**
 * Copyright 2025 (c) Christian von Arnim, ISW University of Stuttgart (for umati and VDW e.V.)
 * Copyright 2025 (c) Marc Fischer, ISW University of Stuttgart (for umati and VDW e.V.)
 * Copyright 2025 (c) Moritz Walker, ISW University of Stuttgart (for umati and VDW e.V.)
 */

import { createStore, StoreApi } from 'zustand';
import { NodeIdentifier, ns0, nsIA, PubSubAddressSpace, UaNode } from './PubSubAddressSpace';
import PubSubTopics from './PubSubTopics';
import {
  CompareNodeId,
  DataFrame,
  MetadataFieldChild,
  MetadataFieldReferences,
  MetadataFrame,
  NodeClassType,
  NodeId,
  NodeIdIdentifierType,
  QualifiedName,
  ReferenceDescription,
} from './PubSubTypes';

type _FieldWithReference = { field: MetadataFieldChild; reference?: ReferenceDescription };
const jobMetaDataRegex = new RegExp('(.*)\/metadata\/(.*)\/ProductionPlan\/(.*)');
const jobDataRegex = new RegExp('(.*)\/data\/(.*)\/ProductionPlan\/(.*)');

let firstCallFixBrokenQualifiedName = true;
function fixBrokenQualifiedName(browseName: QualifiedName): QualifiedName {
  if (firstCallFixBrokenQualifiedName) {
    // TODO: Investigate why this happens
    // console.error('Qualified name is broken in metadata for BrowseName, fix required.');
    firstCallFixBrokenQualifiedName = false;
  }
  const wrongBrowseName = browseName as unknown as { Name: string; Uri: number };
  const Name = wrongBrowseName.Name;
  const NamespaceIndex = wrongBrowseName.Uri;
  return { Name, NamespaceIndex };
}

const wellKnownHierarchicalReferences: { [nsUri: string]: Set<NodeIdIdentifierType> } = {
  'http://opcfoundation.org/UA/': new Set([
    ns0.HierarchicalReferences,
    ns0.HasChild,
    ns0.Organizes,
    ns0.Aggregates,
    ns0.HasSubtype,
    ns0.HasProperty,
    ns0.HasComponent,
    ns0.HasOrderedComponent,
    ns0.HasAddIn,
  ]),
  'http://opcfoundation.org/UA/IA/': new Set([nsIA.HasStatisticsComponent]),
};

interface PubSubMessageStoreState {
  addrSpace: PubSubAddressSpace;
  _metadataStore: { [key: string]: MetadataFrame };
  _topicToNodeId: { [topic: string]: NodeId };
  _topicToValues: {
    [key: string]: {
      nodeIdentifier: NodeIdentifier;
      propName: string;
    }[];
  };
  setAddressSpace: (addrSpace: PubSubAddressSpace) => void;
  processMetadataFrame: (topic: string, frame?: MetadataFrame) => void;
  processDataFrame: (topic: string, frame?: DataFrame) => void;
  tryAddressSpaceReconstruction: () => boolean;
  _addNodesByMetadataFrame: (topic: string, metaDataFrame: MetadataFrame) => boolean;
  _tryReconstructSubtree: (
    startTopic: string,
    startReferenceDescription: ReferenceDescription,
    namespaces?: string[],
  ) => boolean;
  _reconstructAddressSpace: (
    topicNodeReferences: { [topic: string]: ReferenceDescription },
    processedMetadataFrames: {
      [topic: string]: {
        fields: {
          [key: string]: _FieldWithReference;
        };
        foreignReferences: ReferenceDescription[];
      };
    },
  ) => void;
  _splitFieldAndForeignReferences: (
    metadataFrame: MetadataFrame,
    topic: string,
  ) => {
    foreignReferences: ReferenceDescription[];
    fields: { [key: string]: _FieldWithReference };
  };
  _expectedNodes: (
    topic: string,
    foreignReferences: ReferenceDescription[],
  ) => { [topic: string]: ReferenceDescription };
  _tryProcessUnprocessedMetadata: () => boolean;
  _filterHierarchicalReferences: (foreignReferences: ReferenceDescription[]) => ReferenceDescription[];
}

export type PubSubMesageHandling = StoreApi<PubSubMessageStoreState>;

export const createPubSubMesageHandlingStore = (addrSpace: PubSubAddressSpace) =>
  createStore<PubSubMessageStoreState>((set, get) => ({
    addrSpace: addrSpace,
    _metadataStore: {},
    _topicToNodeId: {},
    _topicToValues: {},
    setAddressSpace: (addrSpace: PubSubAddressSpace) => set({ addrSpace }),
    processMetadataFrame: (topic, frame) => {
      const { addrSpace, _metadataStore } = get();
      if (!addrSpace.getState().isAddressSpaceReconstructed()) {
        if (frame === undefined) {
          delete _metadataStore[topic];
        } else {
          _metadataStore[topic] = frame;
        }
        set({ _metadataStore: { ..._metadataStore } });
        return;
      }

      if (topic in _metadataStore) {
        if (frame === undefined) {
          /// todo: Remove subtree from the address space (also invalidate all the metadata frames)
          //this._removeNodesByTopic(topic);
          console.error(
            `Removal of Nodes in the address space not implemented yet. Cannot process metadata frames at ${topic}.`,
          );
          delete _metadataStore[topic];
        } else {
          /// todo: Update address space
          // this._updateNodesByMetadataFrame(topic, frame);
          // TODO: investigate:
          // console.debug(`Updates of address space not implemented yet. Cannot process metadata frame at ${topic}.`);
          _metadataStore[topic] = frame;
        }
        set({ _metadataStore: { ..._metadataStore } });
      } else {
        if (frame !== undefined) {
          get()._addNodesByMetadataFrame(topic, frame);
          _metadataStore[topic] = frame;
          set({ _metadataStore: { ..._metadataStore } });
        }
      }
    },
    processDataFrame: (topic, frame) => {
      const { addrSpace, _topicToValues } = get();
      if (_topicToValues[topic] === undefined) {
        // We only warn about missing topic mapping, if the topic is not a dynamic job.
        if (!jobDataRegex.test(topic)) {
          console.warn(`No topic mapping for topic '${topic}' found. Cannot process data frame.`);
        }
        return;
      }
      const mappings = _topicToValues[topic];
      for (const mapping of mappings) {
        if (frame === undefined) {
          addrSpace.getState().updateNodeValue(mapping.nodeIdentifier, undefined);
          continue;
        }
        const value = frame.Messages[0]?.Payload[mapping.propName];
        if (value !== undefined) {
          addrSpace.getState().updateNodeValue(mapping.nodeIdentifier, value);
        }
      }
    },
    tryAddressSpaceReconstruction: () => {
      const { addrSpace, _metadataStore, _topicToNodeId, _topicToValues } = get();
      if (addrSpace.getState().isAddressSpaceReconstructed()) {
        console.error('Address space already reconstructed.');
        return true;
      }
      if (addrSpace.getState().numberOfNodes() > 0) {
        throw new Error('AdressSpace not empty.');
      }
      const rootTopic = PubSubTopics.Metadata(addrSpace.getState().topicPath);
      const rootMetadataFrame = _metadataStore[rootTopic];
      if (rootMetadataFrame === undefined) {
        return false;
      }
      const namespaces: string[] = rootMetadataFrame.MetaData.Namespaces;
      const rootReferenceDescription: ReferenceDescription = {
        BrowseName: { Name: addrSpace.getState().topicPath.split('/').pop() ?? 'Undefined' },
        DisplayName: { Text: addrSpace.getState().topicPath.split('/').pop() ?? 'Undefined' },
        IsForward: true,
        NodeClass: NodeClassType.OBJECT,
        NodeId: { Id: 0 },
        ReferenceTypeId: { Id: 0 },
      };
      const successReconstruct = get()._tryReconstructSubtree(rootTopic, rootReferenceDescription, namespaces);
      if (successReconstruct === false) {
        return false;
      }
      const rootNode = addrSpace.getState().getNodeByNodeId({ Id: 0 });
      if (rootNode === undefined) {
        throw 'Internal error, no root node created.';
      }
      addrSpace.getState().setRootNode(rootNode);
      addrSpace.getState().setAddressSpaceReconstructed();
      get()._tryProcessUnprocessedMetadata();
      console.info(
        `%c Address Space reconstructed with ${addrSpace.getState().numberOfNodes()} nodes.`,
        'background: #0ddd4f; color: #000000',
      );
      return true;
    },
    _addNodesByMetadataFrame: (topic, metaDataFrame) => {
      const { addrSpace, _splitFieldAndForeignReferences, _filterHierarchicalReferences, _tryReconstructSubtree } =
        get();
      const { fields: _, foreignReferences } = _splitFieldAndForeignReferences(metaDataFrame, topic);
      const revReferences = foreignReferences.filter((r) => r.IsForward === false);

      if (revReferences.length === 0) {
        if (!jobMetaDataRegex.test(topic)) {
          console.error('Frame without rev reference, unknown position in the address space.', topic);
        }
        return false;
      }
      const revHierarchicalReferences = _filterHierarchicalReferences(revReferences);
      const removeNamespacePrefix = (name: string) => {
        const parts = name.split(':');
        if (parts.length > 1) {
          return parts[1];
        } else {
          return name;
        }
      };
      if (revHierarchicalReferences.length === 0) {
        console.error('Frame without hierarchical reverse Reference, unknown position in the address space.', topic);
        return false;
      } else if (revHierarchicalReferences.length > 1) {
        console.warn('Multiple hierarchical reverse references for at topic', topic);
      }

      let ret: boolean[] = [];
      for (const revReference of revHierarchicalReferences) {
        const revNode = addrSpace.getState().getNodeByNodeId(revReference.NodeId);
        if (revNode === undefined) {
          console.log(`Unknown rev node`, revReference.NodeId);
          continue;
        }
        const name = topic.split('/').at(-1);
        let refToNode: ReferenceDescription | undefined = undefined;
        for (const ref of revNode.References) {
          if (ref.isForward && name && ref.TargetNode.BrowseName.Name === removeNamespacePrefix(name)) {
            let bn = { ...ref.TargetNode.BrowseName };
            bn.Name = name;
            refToNode = {
              BrowseName: bn,
              IsForward: true,
              DisplayName: { Text: 'Not used' },
              NodeClass: ref.TargetNode.nodeClass,
              NodeId: ref.TargetNode.NodeId,
              ReferenceTypeId: ref.ReferenceTypeId,
            };
          }
        }
        if (refToNode === undefined) {
          console.warn(`Topic '${topic}' could not be processed, Name=${name}`);
          console.log(revNode);
          continue;
        }
        ret.push(_tryReconstructSubtree(topic, refToNode));
      }
      let val = ret.reduce((prev, curr) => {
        return prev && curr;
      }, true);
      if (!val) {
        console.warn('All success?: ', ret);
      }
      return val;
    },
    _tryReconstructSubtree: (startTopic, startReferenceDescription, namespaces) => {
      const {
        addrSpace,
        _metadataStore,
        _splitFieldAndForeignReferences,
        _expectedNodes,
        _reconstructAddressSpace,
        _topicToNodeId,
      } = get();
      if (addrSpace.getState().namespaces.length === 0 && (namespaces === undefined || namespaces.length === 0)) {
        console.error('Namespace array is required.');
        return false;
      }
      let requiredTopics: string[] = [startTopic];
      // Used to get the nodeIds of the nodes
      let topicNodeReferences: { [topic: string]: ReferenceDescription } = {};
      topicNodeReferences[startTopic] = startReferenceDescription;
      const processedMetadataFrames: {
        [topic: string]: {
          fields: {
            [key: string]: _FieldWithReference;
          };
          foreignReferences: ReferenceDescription[];
        };
      } = {};
      while (requiredTopics.length > 0) {
        const currentTopic = requiredTopics.pop();
        if (currentTopic === undefined) {
          throw 'Internal error, undefined elements in requiredTopics';
        }
        if (currentTopic in processedMetadataFrames) {
          // Already processed?? Invalid references??
          console.error(`Topic already processed: '${currentTopic}'`);
          continue;
        }
        const metaDataFrame = _metadataStore[currentTopic];
        if (metaDataFrame === undefined) {
          if (!jobMetaDataRegex.test(currentTopic)) {
            console.warn(`No metadata frame for topic '${currentTopic}' found, metadata incomplete`);
            return false;
          } else {
            console.error(`Dynamic Job Topic ${currentTopic} detected, will skip over it.`);
          }
        }
        const { fields, foreignReferences } = _splitFieldAndForeignReferences(metaDataFrame, currentTopic);
        processedMetadataFrames[currentTopic] = { fields, foreignReferences };
        const expectedNodes = _expectedNodes(currentTopic, foreignReferences);
        requiredTopics.push(...Object.keys(expectedNodes));
        topicNodeReferences = { ...topicNodeReferences, ...expectedNodes };
      }
      ///todo check orphan metadata by matching processed metadata and received

      // All metadata found, create nodes and references
      // Store topic/value assignment
      if (namespaces !== undefined) {
        addrSpace.getState().setNamespaces(namespaces);
      }
      _reconstructAddressSpace(topicNodeReferences, processedMetadataFrames);
      for (const t in topicNodeReferences) {
        if (t in _topicToNodeId && !CompareNodeId(_topicToNodeId[t], topicNodeReferences[t].NodeId)) {
          console.error(
            'Topic changed Nodeid, is this a different OPC UA Namespace?',
            t,
            _topicToNodeId[t],
            topicNodeReferences[t].NodeId,
          );
        }
        _topicToNodeId[t] = topicNodeReferences[t].NodeId;
      }
      set({ _topicToNodeId: { ..._topicToNodeId } });
      return true;
    },
    _reconstructAddressSpace: (topicNodeReferences, processedMetadataFrames) => {
      const { addrSpace, _topicToValues } = get();
      const topicNodeMap: { [topic: string]: UaNode } = {};
      // Add all scaffold nodes (that have it's own topics)
      for (const topic in topicNodeReferences) {
        const refDesc = topicNodeReferences[topic];
        const existingNode = addrSpace.getState().getNodeByNodeId(refDesc.NodeId);
        if (existingNode !== undefined) {
          topicNodeMap[topic] = existingNode;
        } else {
          const node = addrSpace
            .getState()
            .createUaNodeById(refDesc.NodeId, fixBrokenQualifiedName(refDesc.BrowseName), refDesc.NodeClass);
          addrSpace.getState().addNode(node, false);
          topicNodeMap[topic] = node;
        }
      }
      const topicMetadataPrefix = PubSubTopics.Metadata(addrSpace.getState().topicPath);
      const topicDataPrefix = PubSubTopics.Data(addrSpace.getState().topicPath);
      for (const metadataTopic in processedMetadataFrames) {
        const valuePropertiesToNodeIdentifier: { nodeIdentifier; propName }[] = [];
        const { fields, foreignReferences } = processedMetadataFrames[metadataTopic];
        const scaffoldNode = topicNodeMap[metadataTopic];
        if (scaffoldNode === undefined) {
          throw new Error('Internal error, scaffold node does not exist.');
        }
        for (const fieldName in fields) {
          const field = fields[fieldName];
          const refDesc = field.reference;
          if (refDesc === undefined) {
            console.error(`No Reference found for field ${fieldName} in ${metadataTopic}`);
            continue;
          }
          /// todo How to check for addressspace complete with variable types??
          /// todo check if a scaffoldNode for this field exists
          const node = addrSpace
            .getState()
            .createUaNodeById(refDesc.NodeId, fixBrokenQualifiedName(refDesc.BrowseName), refDesc.NodeClass);
          const nodeIdentifier = addrSpace.getState().addNode(node, false);
          valuePropertiesToNodeIdentifier.push({ propName: fieldName, nodeIdentifier: nodeIdentifier });
          addrSpace.getState().addReference(scaffoldNode, node, refDesc.ReferenceTypeId, refDesc.IsForward);
        }
        for (const ref of foreignReferences) {
          const targetNode = addrSpace.getState().getNodeByNodeId(ref.NodeId);
          if (targetNode !== undefined) {
            addrSpace.getState().addReference(scaffoldNode, targetNode, ref.ReferenceTypeId, ref.IsForward);
          } else {
            addrSpace.getState().addExternalReference(scaffoldNode, ref.NodeId, ref.ReferenceTypeId, ref.IsForward);
          }
        }
        const dataTopic = metadataTopic.replace(topicMetadataPrefix, topicDataPrefix);
        _topicToValues[dataTopic] = valuePropertiesToNodeIdentifier;
      }
      set({ _topicToValues: { ..._topicToValues } });
    },
    _splitFieldAndForeignReferences: (metadataFrame, topic: string) => {
      /// todo rename "virtualId" to "references"/ "nodeReferences"?
      /// with all references as array in one field of a fixed name, e.g. references
      const referecesField = metadataFrame?.MetaData?.Fields.find(
        (f) => f.Name === 'virtualId',
      ) as MetadataFieldReferences;
      /// todo add NodeId to fields?
      const fieldsList = metadataFrame?.MetaData?.Fields.filter((f) => f.Name !== 'virtualId') as MetadataFieldChild[];

      if (referecesField === undefined) {
        console.error(`Metadataframe on topic '${topic}' does not contain references or is undefined.`, metadataFrame);
      }
      let fieldsMap: { [key: string]: _FieldWithReference } = {};
      if (fieldsList !== undefined) {
        for (const f of fieldsList) {
          if (f.Name in fieldsMap) {
            console.error(`Field with name ${f.Name} defined multiple times.`);
            continue;
          }
          fieldsMap[f.Name] = { field: f };
        }
      }
      const updateField = (ref: ReferenceDescription): boolean => {
        if (ref.BrowseName === undefined) {
          console.error('Reference without BrowseName:', ref);
          return false;
        }
        const name = ref.BrowseName.Name;
        if (!(name in fieldsMap)) {
          return false;
        }
        const fieldMapEntry = fieldsMap[name];
        if (fieldMapEntry.reference === undefined) {
          fieldMapEntry.reference = ref;
        } else {
          console.log(`Refernce for field '${name}' already set.`, fieldMapEntry, ref);
        }
        return true;
      };

      let foreignReferences: ReferenceDescription[] = [];
      // loop references, ignore properties
      for (const prop of referecesField?.Properties ?? []) {
        const keyName = prop?.Key?.Name;
        if (keyName === 'relations') {
          const listVal = prop?.Value;
          if (!Array.isArray(listVal?.Body)) {
            console.error(
              'Property "relations" hat nicht den erwarteten Listentyp (Type 24) oder Body ist kein Array.',
            );
            break;
          }
          for (const item of listVal.Body) {
            if (item?.Type !== 22 || item?.Body?.TypeId?.Id !== 518 || !item?.Body?.Body) {
              console.error(
                'Listenelement in "relations" hat nicht den erwarteten ExtensionObject-Typ (Type 22 / TypeId 518).',
              );
              continue;
            }
            const ref = item.Body.Body;
            if (!updateField(ref)) {
              foreignReferences.push(ref);
            }
          }
        } else {
          // Nicht relevante Properties werden protokolliert
          console.debug('Unknown property in list.', keyName);
        }
      }
      return {
        foreignReferences: foreignReferences,
        fields: fieldsMap,
      };
    },
    _expectedNodes: (topic, foreignReferences) => {
      const { _filterHierarchicalReferences } = get();
      let hierarchicalRefs = _filterHierarchicalReferences(foreignReferences);
      /// todo use nodeIds of other metadata for a better identification
      let ret: { [topic: string]: ReferenceDescription } = {};
      for (const ref of hierarchicalRefs) {
        if (!ref.IsForward) {
          continue;
        }
        if (ref.BrowseName === undefined) {
          console.error('Reference without BrowseName:', ref);
          continue;
        }

        const topicWithoutNs = `${topic}/${ref.BrowseName.Name}`;
        const topicWithNs = `${topic}/${ref.BrowseName.Uri}:${ref.BrowseName.Name}`;
        const expectWithoutNs = topic.indexOf(':') == -1;

        const expectedTopic = expectWithoutNs ? topicWithoutNs : topicWithNs;

        if (expectedTopic in ret) {
          if (expectWithoutNs) {
            ret[topicWithNs] = ref;
          }
          continue;
        }
        ret[expectedTopic] = ref;
      }
      return ret;
    },
    _tryProcessUnprocessedMetadata: () => {
      const { _metadataStore, _topicToNodeId, _addNodesByMetadataFrame } = get();
      const recvTopics = Object.keys(_metadataStore).sort((a, b) => a.length - b.length);
      let ret = true;
      for (const t of recvTopics) {
        if (!(t in _topicToNodeId)) {
          const processSuccess = _addNodesByMetadataFrame(t, _metadataStore[t]);
          if (!processSuccess) {
            if (!jobMetaDataRegex.test(t)) {
              console.error('Processing of topic not successfull: ', t);
            }
          }
          ret = ret && processSuccess;
        }
      }
      return ret;
    },
    _filterHierarchicalReferences: (foreignReferences) => {
      const isWellKnownHierarchicalReference = (nodeId: NodeId): boolean => {
        /// todo allow custom definitions for references
        let namespaceUri = 'http://opcfoundation.org/UA/';
        if (nodeId.Namespace !== undefined) {
          const { _metadataStore, addrSpace } = get();
          const rootTopic = PubSubTopics.Metadata(addrSpace.getState().topicPath);
          const namespaces = _metadataStore[rootTopic].MetaData.Namespaces;
          namespaceUri = namespaces[nodeId.Namespace];
        }
        if (namespaceUri in wellKnownHierarchicalReferences) {
          return wellKnownHierarchicalReferences[namespaceUri].has(nodeId.Id);
        } else {
          console.error(`wellKnownHierarchicalReferences not defined for namespace ${namespaceUri}`);
        }
        return false;
      };
      let hierarchicalRefs = foreignReferences.filter((r) => isWellKnownHierarchicalReference(r.ReferenceTypeId));
      return hierarchicalRefs;
    },
  }));
